import React, { useMemo, useState } from 'react';
import { AttributeDef, AttributeInputType, AttributeOption } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';
import './Sections.css';

// Attribute Master — the definition side of "Attribute Settings" in the Product
// Master. An attribute is a named property a product can carry (Size, Colour,
// Flavour, Brand, Material ...). List attributes hold a set of allowed options,
// each with an optional price delta; text / number / boolean / date attributes
// take a free value on the product.
//
// Two flags drive the Product Master:
//   • useForVariants — the attribute becomes a variant axis, so the product can
//     generate its variant matrix (Small/Red, Small/Blue, ...) with prices
//     derived from the base price + each option's extra price.
//   • categories     — limits the attribute to certain categories; empty means
//     it applies to every product.

const INPUT_TYPES: { value: AttributeInputType; label: string }[] = [
  { value: 'list', label: 'List (choose from options)' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
];

const blankOption = (): AttributeOption => ({
  id: Math.random().toString(36).slice(2),
  value: '',
  extraPrice: 0,
  active: true,
});

const blankForm = () => ({
  name: '',
  regionalName: '',
  code: '',
  inputType: 'list' as AttributeInputType,
  required: false,
  multiSelect: false,
  useForVariants: false,
  showOnBill: false,
  filterable: true,
  active: true,
  serialNo: '',
  categories: [] as string[],
});

type Form = ReturnType<typeof blankForm>;

const Attributes: React.FC = () => {
  const {
    attributes,
    products,
    categories: categoryMaster,
    addAttribute,
    updateAttribute,
    deleteAttribute,
  } = usePos();
  const [editing, setEditing] = useState<AttributeDef | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState<Form>(blankForm());
  const [options, setOptions] = useState<AttributeOption[]>([blankOption()]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((prev) => ({ ...prev, [k]: v }));

  // Top-level category names, used for the "applies to" picker.
  const categoryNames = useMemo(() => {
    const names = [...categoryMaster]
      .filter((c) => !c.parentCategory)
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name))
      .map((c) => c.name);
    const seen = new Set(names);
    products.forEach((p) => {
      if (p.category && !seen.has(p.category)) {
        seen.add(p.category);
        names.push(p.category);
      }
    });
    return names;
  }, [categoryMaster, products]);

  const countFor = (id: string) =>
    products.filter((p) => p.attributes?.some((a) => a.attributeId === id && a.values.length)).length;

  const sorted = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    return [...attributes]
      .filter((a) => !t || a.name.toLowerCase().includes(t) || (a.code ?? '').toLowerCase().includes(t))
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name));
  }, [attributes, listSearch]);

  const reset = () => {
    setEditing(null);
    setF(blankForm());
    setOptions([blankOption()]);
  };

  const setOption = (id: string, k: keyof AttributeOption, v: string | boolean) =>
    setOptions((prev) =>
      prev.map((o) => (o.id === id ? { ...o, [k]: k === 'extraPrice' ? parseFloat(v as string) || 0 : v } : o))
    );
  const addOption = () => setOptions((prev) => [...prev, blankOption()]);
  const delOption = (id: string) => setOptions((prev) => prev.filter((o) => o.id !== id));
  const moveOption = (i: number, dir: -1 | 1) =>
    setOptions((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const toggleCategory = (name: string) =>
    setF((prev) => ({
      ...prev,
      categories: prev.categories.includes(name)
        ? prev.categories.filter((c) => c !== name)
        : [...prev.categories, name],
    }));

  const buildData = (): Omit<AttributeDef, 'id'> => ({
    name: f.name.trim(),
    regionalName: f.regionalName.trim() || undefined,
    code: f.code.trim() || undefined,
    inputType: f.inputType,
    options:
      f.inputType === 'list'
        ? options
            .filter((o) => o.value.trim())
            .map((o, i) => ({
              id: o.id,
              value: o.value.trim(),
              extraPrice: o.extraPrice || undefined,
              serialNo: i + 1,
              active: o.active !== false,
            }))
        : [],
    required: f.required,
    multiSelect: f.inputType === 'list' ? f.multiSelect : false,
    useForVariants: f.inputType === 'list' ? f.useForVariants : false,
    showOnBill: f.showOnBill,
    filterable: f.filterable,
    categories: f.categories.length ? f.categories : undefined,
    serialNo: f.serialNo ? parseInt(f.serialNo, 10) : undefined,
    active: f.active,
  });

  const save = () => {
    const data = buildData();
    if (!data.name) return alert('Attribute name is required.');
    const clash = attributes.some(
      (a) => a.id !== editing?.id && a.name.trim().toLowerCase() === data.name.toLowerCase()
    );
    if (clash) return alert(`An attribute named "${data.name}" already exists.`);
    if (data.inputType === 'list' && data.options.length === 0)
      return alert('A list attribute needs at least one option.');
    const dupe = data.options
      .map((o) => o.value.toLowerCase())
      .find((v, i, arr) => arr.indexOf(v) !== i);
    if (dupe) return alert(`Duplicate option value: ${dupe}`);
    if (data.multiSelect && data.useForVariants)
      return alert('A multi-select attribute cannot be used as a variant axis.');

    if (editing) updateAttribute({ ...editing, ...data });
    else addAttribute(data);
    reset();
  };

  const edit = (a: AttributeDef) => {
    setEditing(a);
    setF({
      name: a.name,
      regionalName: a.regionalName ?? '',
      code: a.code ?? '',
      inputType: a.inputType,
      required: !!a.required,
      multiSelect: !!a.multiSelect,
      useForVariants: !!a.useForVariants,
      showOnBill: !!a.showOnBill,
      filterable: a.filterable !== false,
      active: a.active !== false,
      serialNo: a.serialNo != null ? String(a.serialNo) : '',
      categories: a.categories ? [...a.categories] : [],
    });
    setOptions(a.options.length ? a.options.map((o) => ({ ...o })) : [blankOption()]);
  };

  const remove = () => {
    if (!editing) return;
    const inUse = countFor(editing.id);
    const msg = inUse
      ? `Delete "${editing.name}"? It will also be removed from ${inUse} product(s).`
      : 'Delete this attribute?';
    if (window.confirm(msg)) {
      deleteAttribute(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* LEFT: attribute list */}
      <div className="pm-list card-rounded">
        <input
          className="pm-list-search"
          placeholder="Search Attribute"
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
        />
        <div className="pm-list-scroll">
          {sorted.map((a) => (
            <button
              key={a.id}
              className={`pm-list-item ${editing?.id === a.id ? 'sel' : ''}`}
              onClick={() => edit(a)}
            >
              <span className="pm-li-name">
                {a.name}
                {a.active === false ? ' (inactive)' : ''}
                {a.useForVariants ? ' · variant axis' : ''}
              </span>
              <span className="pm-li-meta">
                {a.inputType === 'list'
                  ? `${a.options.length} option${a.options.length === 1 ? '' : 's'}: ${a.options
                      .slice(0, 4)
                      .map((o) => o.value)
                      .join(', ')}${a.options.length > 4 ? '…' : ''}`
                  : INPUT_TYPES.find((t) => t.value === a.inputType)?.label}
                {' · '}
                {countFor(a.id)} product{countFor(a.id) === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {sorted.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No attributes yet.
            </p>
          )}
        </div>
      </div>

      {/* RIGHT: add/edit form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? `Edit Attribute — ${editing.name}` : 'New Attribute'}
          </span>
          <div className="pf-top-btns">
            {editing && (
              <button className="pf-btn-grey" onClick={reset}>
                New
              </button>
            )}
            {editing && (
              <button className="pf-btn-orange" onClick={remove}>
                Delete
              </button>
            )}
            <button className="pf-btn-orange" onClick={save}>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r4">
            <div>
              {lbl('Attribute Name *')}
              <input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Size" />
            </div>
            <div>
              {lbl('Regional Name')}
              <input value={f.regionalName} onChange={(e) => set('regionalName', e.target.value)} />
            </div>
            <div>
              {lbl('Code')}
              <input value={f.code} onChange={(e) => set('code', e.target.value)} placeholder="e.g. SZ" />
            </div>
            <div>
              {lbl('Serial No')}
              <input type="number" value={f.serialNo} onChange={(e) => set('serialNo', e.target.value)} />
            </div>
          </div>

          <div className="pf-row r2">
            <div>
              {lbl('Input Type')}
              <select
                value={f.inputType}
                onChange={(e) => set('inputType', e.target.value as AttributeInputType)}
              >
                {INPUT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div />
          </div>

          <div className="pf-checks">
            <label>
              <input type="checkbox" checked={f.required} onChange={(e) => set('required', e.target.checked)} />{' '}
              Required
            </label>
            <label title="Products may hold several values of this attribute">
              <input
                type="checkbox"
                checked={f.multiSelect}
                disabled={f.inputType !== 'list'}
                onChange={(e) => set('multiSelect', e.target.checked)}
              />{' '}
              Multi-select
            </label>
            <label title="Offer this attribute as a variant axis in the Product Master">
              <input
                type="checkbox"
                checked={f.useForVariants}
                disabled={f.inputType !== 'list'}
                onChange={(e) => set('useForVariants', e.target.checked)}
              />{' '}
              Use for Variants
            </label>
            <label>
              <input type="checkbox" checked={f.showOnBill} onChange={(e) => set('showOnBill', e.target.checked)} />{' '}
              Show on Bill
            </label>
            <label>
              <input type="checkbox" checked={f.filterable} onChange={(e) => set('filterable', e.target.checked)} />{' '}
              Filterable
            </label>
            <label>
              <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active
            </label>
          </div>

          {f.inputType === 'list' && (
            <>
              <div className="pf-section">Options · extra price adds to the product's selling price</div>
              <table className="sec-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Value</th>
                    <th style={{ width: 120 }}>Extra Price</th>
                    <th style={{ width: 70 }}>Active</th>
                    <th style={{ width: 100 }}>Order</th>
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {options.map((o, i) => (
                    <tr key={o.id}>
                      <td>
                        <input
                          value={o.value}
                          onChange={(e) => setOption(o.id, 'value', e.target.value)}
                          placeholder="e.g. Large"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={o.extraPrice ?? 0}
                          onChange={(e) => setOption(o.id, 'extraPrice', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={o.active !== false}
                          onChange={(e) => setOption(o.id, 'active', e.target.checked)}
                        />
                      </td>
                      <td>
                        <button
                          className="pf-btn-grey"
                          onClick={() => moveOption(i, -1)}
                          disabled={i === 0}
                          title="Move up"
                        >
                          ↑
                        </button>{' '}
                        <button
                          className="pf-btn-grey"
                          onClick={() => moveOption(i, 1)}
                          disabled={i === options.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </td>
                      <td>
                        {options.length > 1 && (
                          <button className="row-del" onClick={() => delOption(o.id)} title="Remove">
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="pf-btn-orange" onClick={addOption} style={{ marginTop: 8 }}>
                + Add Option
              </button>
            </>
          )}

          <div className="pf-section">Applies To · leave empty to apply to every category</div>
          <div className="pf-checks" style={{ flexWrap: 'wrap' }}>
            {categoryNames.map((c) => (
              <label key={c}>
                <input type="checkbox" checked={f.categories.includes(c)} onChange={() => toggleCategory(c)} /> {c}
              </label>
            ))}
            {categoryNames.length === 0 && <p className="muted">No categories defined yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attributes;
